import { DOCUMENT } from '@angular/common';
import { inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '../environments/environment';
import { AuthService } from './auth.service';
import { JWTPayload } from '../../../models/jwt.model';

const FRESHDESK_SCRIPT_ID = 'Freshdesk-js-sdk';

/** Body classes that let global CSS position the two floating launchers:
    `sia-active` while Sia is loaded, `sia-open` while its window is open. */
const SIA_ACTIVE_CLASS = 'sia-active';
const SIA_OPEN_CLASS = 'sia-open';

/** id of the Freshdesk iframe (launcher when collapsed, chat window when open). */
const SIA_FRAME_ID = 'fd_frame';
/** CSS var (px) that pins the chat launcher just left of Sia's open window. */
const CHAT_SHIFT_VAR = '--sia-chat-shift';
/** Gap between Sia's open window and the chat launcher. */
const CHAT_SHIFT_GAP_PX = 16;
/** Let Sia's open animation settle before measuring the window. */
const SIA_OPEN_SETTLE_MS = 400;


/** Max time to wait for user.clear()/destroy before forcing teardown to complete. */
const TEARDOWN_TIMEOUT_MS = 3000;

@Injectable( { providedIn: 'root' } )
export class FreshdeskWebchat {
	private readonly document = inject( DOCUMENT );
	private readonly authService = inject( AuthService );
	private readonly renderer: Renderer2;
	private subscribed = false;

	/**
		* Serialises all widget operations. Every auth event chains onto this,
		* so a logout's teardown always fully completes before the next login's
		* init runs — even if the user logs out and back in within the same tab
		* without a page reload.
		*/
	private queue: Promise<void> = Promise.resolve();

	/** uid of the user the widget is currently initialised for, or null. */
	private activeUid: string | null = null;

	private readonly openedSubject = new Subject<void>();
	/** Emits whenever the Sia widget is opened, so other floating UI can collapse. */
	public readonly opened$: Observable<void> = this.openedSubject.asObservable();

	/** Guards against binding widget event listeners twice on the same instance. */
	private listenersAttached = false;

	constructor () {
		this.renderer = inject( RendererFactory2 ).createRenderer( null, null );
	}

	/**
		* Drives the widget off the auth state: loads it for an eligible user and
		* tears it down on logout (currentUserData emits null). Call once at app start.
		*/
	public init (): void {
		if ( !environment.siaFreshdeskAIAgentEnabled || this.subscribed ) {
			return;
		}
		this.subscribed = true;

		this.authService.currentUserData.subscribe( user => {
			this.queue = this.queue
				.then( () => this.handleAuthChange( user ) )
				.catch( err => console.warn( '[FreshdeskWebchat] auth change handling failed', err ) );
		} );
	}

	private async handleAuthChange ( user: JWTPayload | null ): Promise<void> {
		if ( this.isEligible( user ) ) {
			// Same user re-emitted (e.g. token refresh): nothing to do.
			if ( this.activeUid === user.uid ) {
				return;
			}
			// Different user, or a stale instance from an interrupted logout:
			// always tear down before initialising, so user B never inherits
			// user A's conversation.
			if ( this.activeUid !== null || window.fdWidget ) {
				await this.teardown();
			}
			await this.load( user );
			this.activeUid = user.uid;
			this.renderer.addClass( this.document.body, SIA_ACTIVE_CLASS );
		} else if ( this.activeUid !== null || window.fdWidget ) {
			await this.teardown();
			this.activeUid = null;
			this.renderer.removeClass( this.document.body, SIA_ACTIVE_CLASS );
		}
	}

	/** The widget is for portal (admin) users of any client. */
	private isEligible ( user: JWTPayload | null ): user is JWTPayload {
		return !!user && user.actorType === 'user';
	}

	private load ( user: JWTPayload ): Promise<void> {
		// Script already injected and SDK still alive: just (re)initialise.
		if ( this.document.getElementById( FRESHDESK_SCRIPT_ID ) && window.fdWidget ) {
			this.initWidget( user );
			return Promise.resolve();
		}

		return new Promise<void>( ( resolve, reject ) => {
			// Remove any dead script tag left over from a previous teardown
			// so the browser re-executes the SDK and recreates window.fdWidget.
			this.document.getElementById( FRESHDESK_SCRIPT_ID )?.remove();

			const script = this.renderer.createElement( 'script' ) as HTMLScriptElement;
			script.id = FRESHDESK_SCRIPT_ID;
			script.async = true;
			script.src = `${ environment.siaFreshdeskAIAgent.host }/webchat/js/widget.js`;
			script.onload = () => {
				this.initWidget( user );
				resolve();
			};
			script.onerror = () => reject( new Error( 'Freshdesk widget script failed to load' ) );
			this.renderer.appendChild( this.document.head, script );
		} );
	}

	private initWidget ( user: JWTPayload ): void {
		const { token, host, widgetId } = environment.siaFreshdeskAIAgent;
		window.fdWidget?.init( {
			token,
			host,
			widgetId,
			// hideChatButton: the header launcher (nav-bar) is the only entry point —
			// suppress Freshdesk's own floating bottom-right bubble.
			config: { fullscreen: false, headerProperty: { hideChatButton: true } },
			contactProperties: {
				email: user.email,
				unique_external_id: user.uid,
			},
		} );
		this.attachListeners();
	}

	/** Opens the Sia widget. Safe to call when it isn't loaded (no-ops).
		Sets the open state immediately (rather than waiting for the slower
		widget:opened event) so toggle() reads the right state right away. */
	public open (): void {
		try {
			window.fdWidget?.open?.();
		} catch { /* widget may not be ready */ }
		this.renderer.addClass( this.document.body, SIA_OPEN_CLASS );
	}

	/** Collapses the Sia widget if it's open. Safe to call when it isn't loaded.
		Clears the open state immediately (rather than waiting for the slower
		widget:closed event) so the chat launcher slides back without lag. */
	public close (): void {
		try {
			window.fdWidget?.close?.();
		} catch { /* widget may not be ready */ }
		this.renderer.removeClass( this.document.body, SIA_OPEN_CLASS );
	}

	/** Opens Sia if it's closed, closes it if it's open. Reads the body class
		set by open()/close() as the single source of truth for current state. */
	public toggle (): void {
		if ( this.document.body.classList.contains( SIA_OPEN_CLASS ) ) {
			this.close();
		} else {
			this.open();
		}
	}

	/**
		* Mirrors the widget's open state onto a body class (so global CSS can
		* shift the in-app chat launcher clear of Sia's window) and notifies the
		* in-app chat, via openedSubject, to close when Sia opens.
		*/
	private attachListeners (): void {
		const fd = window.fdWidget;
		if ( !fd?.on || this.listenersAttached ) {
			return;
		}
		fd.on( 'widget:opened', () => {
			this.openedSubject.next();
			this.pinChatLauncherToWindow();
			this.renderer.addClass( this.document.body, SIA_OPEN_CLASS );
			// Re-measure once the open animation settles, in case the first
			// measurement caught the window mid-transition.
			setTimeout( () => this.pinChatLauncherToWindow(), SIA_OPEN_SETTLE_MS );
		} );
		fd.on( 'widget:closed', () => this.renderer.removeClass( this.document.body, SIA_OPEN_CLASS ) );
		this.listenersAttached = true;
	}

	/**
		* Measures Sia's open window and pins the in-app chat launcher just to its
		* left via a CSS variable. Using the window's real pixel geometry keeps the
		* gap stable regardless of viewport width or dev-tools docking. If the frame
		* can't be measured the CSS fallback applies.
		*/
	private pinChatLauncherToWindow (): void {
		const frame = this.document.getElementById( SIA_FRAME_ID );
		if ( !frame ) {
			return;
		}
		const rightPx = Math.round( window.innerWidth - frame.getBoundingClientRect().left + CHAT_SHIFT_GAP_PX );
		this.document.body.style.setProperty( CHAT_SHIFT_VAR, `${ rightPx }px` );
	}

	/**
		* Fully ends the current visitor session
		* Never rejects, and never hangs longer than TEARDOWN_TIMEOUT_MS.
		*/
	public teardown (): Promise<void> {
		return new Promise<void>( resolve => {
			const fd = window.fdWidget;

			const finalize = () => {
				try {
					fd?.destroy?.();
				} catch { /* widget may already be gone */ }
				this.purgeWidgetState();
				window.fdWidget = undefined;
				this.listenersAttached = false;
				this.renderer.removeClass( this.document.body, SIA_OPEN_CLASS );
				this.renderer.removeClass( this.document.body, SIA_ACTIVE_CLASS );
				resolve();
			};

			if ( !fd ) {
				this.purgeWidgetState();
				resolve();
				return;
			}

			// Safety net: don't let logout hang if the widget never responds.
			const timeout = setTimeout( finalize, TEARDOWN_TIMEOUT_MS );
			const done = () => {
				clearTimeout( timeout );
				finalize();
			};

			try {
				const cleared = fd.user?.clear?.();
				if ( cleared && typeof cleared.then === 'function' ) {
					cleared.then( done, done ); // clear can reject for anonymous users — destroy anyway
				} else {
					done();
				}
			} catch {
				done();
			}
		} );
	}

	/**
		* Removes the SDK script tag, any storage entries keyed by the widget
		* token/widgetId (how fdWidget persists the session)
		*/
	private purgeWidgetState (): void {
		this.document.getElementById( FRESHDESK_SCRIPT_ID )?.remove();

		const { token, widgetId } = environment.siaFreshdeskAIAgent;
		const identifiers = [ token, widgetId ]
			.filter( Boolean )
			.map( id => String( id ).toLowerCase() );

		for ( const storage of [ window.localStorage, window.sessionStorage ] ) {
			try {
				Object.keys( storage )
					.filter( key => {
						const k = key.toLowerCase();
						return identifiers.some( id => k.includes( id ) );
					} )
					.forEach( key => storage.removeItem( key ) );
			} catch { /* storage may be unavailable */ }
		}

	}

	/** Kept for backwards compatibility with existing callers. */
	public destroy (): void {
		void this.teardown();
	}
}